import { Skeleton, Space } from 'antd';
import { useSession } from 'next-auth/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EditUserFormValues } from '../../types/userform';
import { customFieldTypeMap } from '../components/CustomField/CustomField.type';
import ErrorFallback from '../components/Error/ErrorFallback';
import EditUserForm from '../components/Forms/EditUserForm';
import { editUserDefaultValues } from '../constants/forms/userForm';
import useCompanyOwnerId from '../hooks/adminPage/useCompanyOwnerId';
import useUserById, { CustomizedDetailedUser, deriveCustomizedDetailedUser } from '../hooks/useUserById';
import { useRequesterId } from '../layouts/Pages/TicketPagesContentLayout/RequesterDetailContentLayout';
import { DetailedUser, USER_FORM_FIELDS_KEY } from '../model/types';
import { isCurrentUserHavePermissionToEditTargetUser } from '../utils/accountHelper';
import { getUserFriendlyValueFromUser } from '../utils/forms/formFieldsHelper.values';
import { nonNullishValue } from '../utils/helpers';
import {
    EditUserFieldsContainer,
    ShowUserFieldsContainer
} from './Pages/TicketPages/TicketPagesContentLayout/TicketDetailContentLayout/TicketInfoLeftSideTabs/UserFieldsContainer';

const getCustomFields = (userInfo: CustomizedDetailedUser) => ({
    ...Object.fromEntries(
        (userInfo?.fields ?? []).map(({ key, serializedValue, userFriendlyValue, type, value }) => {
            if (!key) return null;

            const hookFormFriendlyFieldKey = key.replace(new RegExp('\\.', 'gi'), '$');

            if (!serializedValue) return [hookFormFriendlyFieldKey, null as string | number];
            if (
                [customFieldTypeMap.USER, customFieldTypeMap.UNTRUSTED_END_USER].includes(
                    type as 'USER' | 'UNTRUSTED_END_USER'
                )
            ) {
                if (!userFriendlyValue && value) {
                    // console.debug(`getCustomFields , userFriendlyValue does not exist for field with fieldKey:`, key);
                    // console.debug(`will cause userFriendlyValue to be shown on UI as:`, serializedValue);
                    return [
                        hookFormFriendlyFieldKey,
                        `${serializedValue}|${
                            getUserFriendlyValueFromUser(value as DetailedUser) ?? (value as DetailedUser)?.phone
                        }`
                    ];
                }
                return [hookFormFriendlyFieldKey, `${serializedValue}|${userFriendlyValue}`];
            }

            if (type === customFieldTypeMap.MULTI_USER) {
                if (!userFriendlyValue) {
                    // console.debug(`getCustomFields , userFriendlyValue does not exist for field with fieldKey:`, key);
                    // console.debug(`will cause userFriendlyValue to be shown on UI as:`, serializedValue);
                    return [hookFormFriendlyFieldKey, `${serializedValue}|${serializedValue}`];
                }
                const userFriendlyValueList = userFriendlyValue.split(', ');
                const hookFormFriendlyFieldValue = serializedValue
                    .split(',')
                    .map((sv, i) => `${sv}|${userFriendlyValueList[i]}`)
                    .toString();
                return [hookFormFriendlyFieldKey, hookFormFriendlyFieldValue];
            }

            return [hookFormFriendlyFieldKey, serializedValue];
        })
    )
});

export default function UserDetailContainer() {
    const {
        data: {
            userInfo: { userId: currentUserId }
        }
    } = useSession();
    const userId = useRequesterId();
    const { data, isLoading, error, mutate } = useUserById(userId?.toString());
    const userInfo = useMemo(() => deriveCustomizedDetailedUser(data), [data]);

    const { data: ownerId } = useCompanyOwnerId();
    const { t } = useTranslation('error');
    const isUserEditable = isCurrentUserHavePermissionToEditTargetUser(userInfo, ownerId);
    const defaultFormValues: EditUserFormValues = useMemo(
        () => ({
            ...editUserDefaultValues,
            fullName: userInfo?.fullName ?? editUserDefaultValues.fullName,
            primaryEmail: userInfo?.primaryEmail ?? editUserDefaultValues.primaryEmail,
            primaryPhone: userInfo?.primaryPhone ?? editUserDefaultValues.primaryPhone,
            role: userInfo?.role?.authority,
            roleToChange: userInfo?.role?.authority,
            secondaryEmails: userInfo?.secondaryEmails?.map((se) => se) ?? editUserDefaultValues.secondaryEmails,
            secondaryPhones: userInfo?.secondaryPhones?.map((sp) => sp) ?? editUserDefaultValues.secondaryPhones,
            userId: (nonNullishValue(userId) && !Number.isNaN(+userId) && +userId) ?? userInfo?.id,
            organization: userInfo?.organization?.id ?? editUserDefaultValues.organization,
            groups: userInfo?.groups?.map((group) => group.id)?.join(','),
            [USER_FORM_FIELDS_KEY]: getCustomFields(userInfo)
            //{ts.ff:'sdf'}
        }),
        [userId, userInfo]
    );

    if (error)
        return (
            <ErrorFallback
                error={error}
                antdProps={{ title: '', subTitle: t('error.no_data') }}
                onActionButtonClick={mutate}
                actionButtonText={t('button.try_again')}
            />
        );
    if (isLoading || !currentUserId || !userId || !userInfo?.id)
        return (
            <>
                <Space className="full-width">
                    <Skeleton.Avatar active size="large" shape="circle" />
                    <Skeleton.Input active size="small" className="full-width" />
                </Space>
                <Space size="small" direction="vertical" style={{ padding: '20px 25px', width: '100%' }}>
                    <Space direction="vertical" className="full-width" size="large">
                        <Skeleton.Input active className="full-width" />
                        <Skeleton.Input active className="full-width" />
                        <Skeleton.Input active className="full-width" />
                        <Skeleton.Input active className="full-width" />
                    </Space>
                </Space>
            </>
        );

    return (
        <EditUserForm key={userId} defaultValues={defaultFormValues}>
            {isUserEditable ? (
                <EditUserFieldsContainer userId={userId?.toString()} />
            ) : (
                <ShowUserFieldsContainer userId={userId?.toString()} />
            )}
        </EditUserForm>
    );
}
